import { useState, useEffect, useCallback, useRef, memo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Heart } from 'lucide-react';

/* Video that pauses automatically when scrolled off-screen */
const AutoPauseVideo = memo(({ src, onClick }: { src: string; onClick?: (e: React.MouseEvent<HTMLVideoElement>) => void }) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !el.paused) {
            try { el.pause(); } catch { /* noop */ }
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      src={src}
      controls
      playsInline
      preload="metadata"
      className="h-full w-full object-cover"
      onClick={onClick}
    />
  );
});
AutoPauseVideo.displayName = 'AutoPauseVideo';

export interface MediaItem {
  url: string;
  type: 'image' | 'video';
}

interface MediaCarouselProps {
  items: MediaItem[];
  className?: string;
  onDoubleTapLike?: () => void;
}

/* Hook: returns handlers + current burst key. Triggers cb on second tap within 300ms. */
const useDoubleTap = (cb?: () => void) => {
  const lastTap = useRef(0);
  const [burstKey, setBurstKey] = useState(0);
  const handleTap = useCallback(() => {
    if (!cb) return false;
    const now = Date.now();
    if (now - lastTap.current < 300) {
      lastTap.current = 0;
      cb();
      setBurstKey((k) => k + 1);
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate?.(15); } catch { /* noop */ }
      }
      return true;
    }
    lastTap.current = now;
    return false;
  }, [cb]);
  return { handleTap, burstKey };
};

const HeartBurst = ({ burstKey }: { burstKey: number }) => {
  if (burstKey === 0) return null;
  return (
    <div
      key={burstKey}
      className="pointer-events-none absolute left-1/2 top-1/2 z-10 animate-heart-burst"
    >
      <Heart className="h-24 w-24 fill-white text-white drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]" />
    </div>
  );
};

const MediaCarousel = ({ items, className, onDoubleTapLike }: MediaCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi]);

  const { handleTap: singleTap, burstKey: singleBurst } = useDoubleTap(onDoubleTapLike);
  const { handleTap: multiTap, burstKey: multiBurst } = useDoubleTap(onDoubleTapLike);

  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    const item = items[0];
    return (
      <div
        className={cn('relative aspect-[4/5] w-full overflow-hidden bg-secondary/60 select-none', className)}
        onClick={() => { singleTap(); }}
      >
        {item.type === 'video' ? (
          <AutoPauseVideo
            src={item.url}
            onClick={(e) => {
              const handled = singleTap();
              if (handled) e.preventDefault();
              e.stopPropagation();
            }}
          />
        ) : (
          <img src={item.url} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" draggable={false} />
        )}
        <HeartBurst burstKey={singleBurst} />
      </div>
    );
  }

  return (
    <div className={cn('relative w-full select-none', className)}>
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {items.map((item, i) => (
            <div
              key={i}
              className="min-w-0 shrink-0 grow-0 basis-full"
              onClick={() => { multiTap(); }}
            >
              <div className="aspect-[4/5] w-full overflow-hidden bg-secondary/60">
                {item.type === 'video' ? (
                  <AutoPauseVideo
                    src={item.url}
                    onClick={(e) => {
                      const handled = multiTap();
                      if (handled) { e.preventDefault(); e.stopPropagation(); }
                    }}
                  />
                ) : (
                  <img src={item.url} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" draggable={false} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <HeartBurst burstKey={multiBurst} />

      {/* Nav arrows */}
      {activeIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); emblaApi?.scrollPrev(); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/60 p-1.5 text-foreground/70 backdrop-blur-sm transition-opacity hover:bg-background/80"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {activeIndex < items.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); emblaApi?.scrollNext(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/60 p-1.5 text-foreground/70 backdrop-blur-sm transition-opacity hover:bg-background/80"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}

      {/* Counter badge */}
      <div className="absolute right-3 top-3 rounded-full bg-background/70 px-2.5 py-0.5 text-xs font-semibold text-foreground backdrop-blur-sm">
        {activeIndex + 1}/{items.length}
      </div>

      {/* Dots */}
      {items.length <= 10 && (
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
          {items.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all duration-200',
                i === activeIndex ? 'w-4 bg-primary' : 'w-1.5 bg-foreground/40'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaCarousel;
